
import axios from "axios";
import extractIdFromGid from "helpers/extract-gid";
import { loopTokenKey, LOOP_STOREFRONT_BASE_URL } from "utils/constants";
import { isValidToken, getCustomerAccessToken } from "utils/oauth";

// These function are used in frontend
const getCustomerShopifyId = () => extractIdFromGid(JSON.parse(sessionStorage.getItem("every-customer"))?.id);

const calculateExpiration = (days) => new Date(new Date().getTime() + (days * 24 * 60 * 60 * 1000));

const setLoopAccessToken = (tokenData) => {
	// Access token expires in 4 days
	const currentToken = getLoopAccessToken();
	const newToken = { ...currentToken, ...tokenData, expiresAt: calculateExpiration(4) };
	if (tokenData.refreshToken) {
		// Refresh token expires in 30 days
		newToken.refreshExpiresAt = calculateExpiration(30);
	}
	window.localStorage.setItem(loopTokenKey, JSON.stringify(newToken));
};

const getLoopAccessToken = () => JSON.parse(window.localStorage.getItem(loopTokenKey)) || {};

const isLoopTokenValid = (token) => {
	if (!token) return false;
	return new Date() < new Date(token.expiresAt);
};

export const withLoopToken = async (callback) => {
	const token = getLoopAccessToken();
	const isValid = isLoopTokenValid(token);

	if (isValid) 
		return callback(token);

	// Token is not valid, refresh it
	const refreshedToken = await refreshLoopToken();
	if (refreshedToken)
		return callback(refreshedToken);
	else
		return new Promise((resolve) => resolve({ success: false }));
};

export const storefrontApiCall = async ({ method = "post", endpoint, data, useToken = true }) => {
	const apiCall = async (auth) => {
		const config = {
			method,
			baseURL: LOOP_STOREFRONT_BASE_URL,
			url: endpoint,
		};

		if (useToken) config.headers = { Authorization: `Bearer ${auth.accessToken}` };
		if (data) config.data = data;

		try {
			const response = await axios(config);
			return response.data;
		} catch (error) {
			return error.response.data;
		}
	};

	return useToken ? await withLoopToken(apiCall) : apiCall();
};

const generateAccessToken = async (customerShopifyId) => {
	const shopifyAccessToken = getCustomerAccessToken();
	if (!isValidToken(shopifyAccessToken)) return;

	// Generate session token
	try {
		const { data: response, status } = await axios.post("/.netlify/functions/generate-session-token", { customerShopifyId });
		if (status !== 200) return;

		if (response.success) {
			// Generate refresh token and access token from sessionToken
			const tokenResponse = await storefrontApiCall({
				endpoint: "/auth/refreshToken",
				data: { sessionToken: response.data.sessionToken },
				useToken: false
			});

			if (tokenResponse.success) {
				setLoopAccessToken(tokenResponse.data);
				return tokenResponse.data;
			}
			return null;
		}
	} catch (err) {
		return null;
	}
};

const rotateLoopAccessToken = async (refreshToken) => {
	const response = await storefrontApiCall({
		endpoint: "/auth/accessToken",
		data: { refreshToken },
		useToken: false
	});

	if (response?.success) {
		setLoopAccessToken(response.data);
		return response.data;
	}

	return null;
};

const refreshLoopToken = async () => {
	const customerShopifyId = getCustomerShopifyId();
	if (!customerShopifyId) return;

	const accessToken = getLoopAccessToken();
	const isValid = isLoopTokenValid(accessToken);
	if (isValid) return;

	let response;
	if (accessToken.refreshToken && new Date(accessToken.refreshExpiresAt) > new Date())
		response = await rotateLoopAccessToken(accessToken.refreshToken);
	else
		response = await generateAccessToken(customerShopifyId);

	return response;
};

export const getLatestSubscription = (subscriptions) => {
	// Sort the subscriptions by createdAt in descending order (latest first)
	const sortedSubscriptions = subscriptions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

	const statusOrder = ["ACTIVE", "PAUSED", "CANCELLED"];

	// Find the latest subscription according to the status order
	for (const status of statusOrder) {
		const subscription = sortedSubscriptions.find(sub => sub.status === status);
		if (subscription)
			return subscription;
	}

	return null;
};